/**
 * ============================================
 * GALLERY MODAL - Galería Global de Imágenes
 * ============================================
 * Modal reutilizable para seleccionar imágenes
 * en cualquier formulario del Master Panel.
 */
import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X, Upload, Trash2, Check, Image, Search, RefreshCw } from 'lucide-react';
import adminService from '../../services/adminService';

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function GalleryModal({ isOpen, onClose, onSelect }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const { data: images = [], isLoading, refetch } = useQuery(
    'adminGallery',
    adminService.getGallery,
    { enabled: isOpen, refetchOnWindowFocus: false }
  );

  const deleteMutation = useMutation(adminService.deleteGalleryImage, {
    onSuccess: () => queryClient.invalidateQueries('adminGallery')
  });

  const filteredImages = images.filter(img =>
    img.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        await adminService.uploadImage(file);
      }
      queryClient.invalidateQueries('adminGallery');
    } catch (e) {
      setUploadError(e?.response?.data?.error || 'Error al subir imagen');
    } finally {
      setUploading(false);
    }
  }, [queryClient]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  const handleSelect = () => {
    if (!selected) return;
    onSelect(selected.name, `${API_BASE}${selected.url}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-[#0d0f26] rounded-3xl border border-slate-700/50 shadow-2xl flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-game-primary/20 flex items-center justify-center">
              <Image size={20} className="text-game-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Galería de Imágenes</h2>
              <p className="text-xs text-slate-500">{images.length} imágenes disponibles</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 p-4 border-b border-slate-800/50 bg-slate-900/30">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Buscar imagen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-game-primary transition-colors"
            />
          </div>

          {/* Refresh */}
          <button onClick={() => refetch()} className="p-2.5 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <RefreshCw size={16} />
          </button>

          {/* Upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2.5 bg-game-primary hover:bg-game-primary/80 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-50"
          >
            <Upload size={16} />
            {uploading ? 'Subiendo...' : 'Subir Imagen'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={e => handleUpload(e.target.files)}
          />
        </div>

        {uploadError && (
          <div className="mx-4 mt-3 px-4 py-2 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-400 text-sm">
            {uploadError}
          </div>
        )}

        {/* Drop Zone + Grid */}
        <div
          className={`flex-1 overflow-y-auto p-4 transition-all ${isDragging ? 'bg-game-primary/10 border-2 border-dashed border-game-primary rounded-2xl m-2' : ''}`}
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="flex flex-col items-center justify-center h-40 text-game-primary">
              <Upload size={40} className="mb-3 animate-bounce" />
              <p className="font-bold text-lg">Suelta las imágenes aquí</p>
            </div>
          )}

          {!isDragging && (
            <>
              {isLoading ? (
                <div className="flex items-center justify-center h-48 text-slate-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-game-primary mr-3" />
                  Cargando galería...
                </div>
              ) : filteredImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500 gap-3">
                  <Image size={40} className="opacity-30" />
                  <p>No hay imágenes en la galería. Sube una para empezar.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {filteredImages.map(img => (
                    <div
                      key={img.name}
                      onClick={() => setSelected(img)}
                      className={`relative group cursor-pointer rounded-2xl overflow-hidden border-2 transition-all aspect-square ${
                        selected?.name === img.name
                          ? 'border-game-primary shadow-lg shadow-game-primary/30 scale-105'
                          : 'border-slate-700/50 hover:border-slate-500'
                      }`}
                    >
                      <img
                        src={`${API_BASE}${img.url}`}
                        alt={img.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23334155" width="100" height="100"/><text fill="%2364748b" font-size="30" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">?</text></svg>'; }}
                      />

                      {/* Selected overlay */}
                      {selected?.name === img.name && (
                        <div className="absolute inset-0 bg-game-primary/30 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-game-primary flex items-center justify-center">
                            <Check size={16} className="text-white" />
                          </div>
                        </div>
                      )}

                      {/* Hover delete */}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          if (window.confirm(`¿Eliminar "${img.name}"?`)) {
                            deleteMutation.mutate(img.name);
                            if (selected?.name === img.name) setSelected(null);
                          }
                        }}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-rose-500/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <Trash2 size={11} />
                      </button>

                      {/* File name tooltip */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs truncate text-center">{img.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-800/50 bg-slate-900/30">
          <div className="text-sm text-slate-500">
            {selected
              ? <span className="text-game-primary font-semibold">✓ Seleccionada: {selected.name}</span>
              : 'Haz clic en una imagen para seleccionarla'}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white text-sm font-bold transition-colors">
              Cancelar
            </button>
            <button
              onClick={handleSelect}
              disabled={!selected}
              className="px-6 py-2 rounded-xl bg-game-primary hover:bg-game-primary/80 text-white text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Usar Imagen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
